/** Live, actor-scoped catalogue reads share in-flight work, never cached access grants. */
import {useEffect, useState} from 'react';
import {useAuth} from '../platform/auth/AuthContext';
import {WorkspacePluginService, WORKSPACE_PLUGINS_CHANGED_EVENT, type WorkspacePluginCatalogue} from '../services/WorkspacePluginService';

const flights = new WeakMap<object, Map<string, Promise<WorkspacePluginCatalogue>>>();
const refreshEvents = new WeakSet<Event>();
let refreshEpoch = 0;
function readCatalogue(actor: object, key: string, workspaceId: string) {
  let pending = flights.get(actor);
  if (!pending) {pending = new Map(); flights.set(actor, pending);}
  const existing = pending.get(key);
  if (existing) return existing;
  const request = WorkspacePluginService.catalogue(workspaceId);
  pending.set(key, request);
  const remove = () => {if (pending.get(key) === request) pending.delete(key);};
  // Both branches handle cleanup without creating an unhandled rejected finally promise.
  void request.then(remove, remove);
  return request;
}

export function useWorkspacePlugins(enabled = true) {
  const {activeWorkspaceId, user, workspaceMemberships} = useAuth();
  const [result, setResult] = useState<{actor: object; key: string; catalogue: WorkspacePluginCatalogue | null; error: string} | null>(null);
  const [request, setRequest] = useState(() => refreshEpoch);
  // Membership values affect access; reference-only refreshes do not.
  const key = JSON.stringify([activeWorkspaceId, user?.id, user?.role, workspaceMemberships]);
  useEffect(() => {
    const refresh = (event: Event) => {
      if (!refreshEvents.has(event)) {refreshEvents.add(event); refreshEpoch += 1;}
      setRequest(refreshEpoch);
    };
    window.addEventListener(WORKSPACE_PLUGINS_CHANGED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(WORKSPACE_PLUGINS_CHANGED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);
  useEffect(() => {
    let active = true;
    if (enabled && activeWorkspaceId && user?.id) {
      const actor = user;
      void readCatalogue(actor, `${key}:${request}`, activeWorkspaceId)
        .then(catalogue => {if (active) setResult({actor,key,catalogue,error:''});})
        .catch(error => {if (active) setResult({actor,key,catalogue:null,error:error instanceof Error ? error.message : 'Unable to load plugins'});});
    }
    return () => {active = false;};
  }, [enabled, activeWorkspaceId, user, key, request]);
  const current = enabled && result?.actor === user && result.key === key ? result : null;
  return {catalogue:current?.catalogue ?? null,error:current?.error ?? '',loading:enabled && !current,retry:() => setRequest(++refreshEpoch)};
}
